// Guided tour for the meal planner: short coach-mark chapters, one per screen, shown once when the
// screen is first seen (spotlight + tooltip, "x / n", Weiter / Überspringen). Paid users also get a
// welcome card on their first Wochenplan visit. Replay: Ziele → "Rundgang ansehen".
// Subscribers only (week, recipes, shop, settings): the setup funnel runs without coach marks, a pop-up
// in front of the decision cost more than it explained (2026-10-11, docs/funnel.md in vhp-planner).
// Progress lives in localStorage `vhp_planner_tour` (this device only).
// The funnel dispatches `vhp:render` ({ detail: { screen } }) after every render; nothing else is shared.
// Console: vhpPlannerTour.start('week'), vhpPlannerTour.reset(). URL: ?tour=0 disables, ?tour=1 resets.
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpPlannerTour) return;
  const funnel = document.querySelector('[data-vhp-funnel]');
  if (!funnel) return;

  const KEY = 'vhp_planner_tour';
  const VERSION = 1;
  const SETTLE_MS = 750;
  const RETRY_MS = 1000;
  const RETRY_MAX = 14;
  const PAD = 6;
  const GAP = 12;
  const MARGIN = 12;

  const paid = () => Boolean(window.vhpPlanner?.config?.paid);
  const reduced = () => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

  // ---------- progress ----------

  const blank = () => ({ v: VERSION, off: false, done: {} });
  const read = () => {
    try {
      const raw = JSON.parse(window.localStorage.getItem(KEY) || 'null');
      if (raw && raw.v === VERSION && raw.done && typeof raw.done === 'object') return raw;
    } catch (e) {
      /* storage blocked: the tour just shows every visit */
    }
    return blank();
  };
  let memo = read();
  const write = () => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(memo));
    } catch (e) {
      /* ignore */
    }
  };
  const markDone = (id) => {
    memo.done[id] = 1;
    write();
  };

  try {
    const flag = new URLSearchParams(window.location.search).get('tour');
    if (flag === '1') {
      memo = blank();
      write();
    } else if (flag === '0') {
      memo.off = true;
      write();
    }
  } catch (e) {
    /* ignore */
  }

  // ---------- chapters ----------
  // sel: CSS selector of the highlighted element (first visible match). A step whose target is missing
  // is skipped. place: 'auto' | 'top' | 'bottom'. A step without sel is a centered card.

  const CHAPTERS = {
    welcome: {
      screen: 'week',
      paid: true,
      steps: [
        {
          title: tr('tour.welcome_title'),
          text: tr('tour.welcome_text'),
          welcome: true,
        },
      ],
    },
    week: {
      screen: 'week',
      paid: true,
      steps: [
        {
          sel: '[data-vhp-week-days]',
          title: tr('tour.day_title'),
          text: tr('tour.day_text'),
          place: 'bottom',
        },
        {
          sel: '[data-vhp-week-meals] .vhp-pick',
          title: tr('tour.recipe_title'),
          text: tr('tour.recipe_text'),
        },
        {
          sel: '[data-vhp-week-meals] .vhp-pick:not(.is-locked)',
          title: tr('tour.swipe_title'),
          text: tr('tour.swipe_text'),
          place: 'bottom',
          enter: () => window.vhpPlannerSwipe?.peek(),
        },
        {
          sel: '[data-vhp-week-meals] [data-vhp-meal-more]',
          title: tr('tour.more_title'),
          text: tr('tour.more_text'),
        },
        {
          sel: '[data-app-plan-fav]',
          title: tr('tour.plan_title'),
          text: tr('tour.plan_text'),
          place: 'bottom',
        },
        {
          sel: '[data-app-plan-views]',
          title: tr('tour.shop_title'),
          text: tr('tour.shop_text'),
          place: 'bottom',
        },
        {
          sel: '[data-app-tabbar]',
          title: tr('tour.anywhere_title'),
          text: tr('tour.anywhere_text'),
          place: 'top',
        },
      ],
    },
    recipes: {
      screen: 'recipes',
      paid: true,
      steps: [
        {
          sel: '[data-vhp-rx-hero]',
          title: tr('tour.new_title'),
          text: tr('tour.new_text'),
          place: 'bottom',
        },
        {
          sel: '.vhp-rx__entries',
          title: tr('tour.favs_title'),
          text: tr('tour.favs_text'),
          place: 'bottom',
        },
      ],
    },
    shop: {
      screen: 'shop',
      paid: true,
      steps: [
        {
          sel: '[data-vhp-shop-household]',
          title: tr('tour.household_title'),
          text: tr('tour.household_text'),
          place: 'bottom',
        },
        {
          sel: '[data-vhp-shop-list] .vhp-shop-line',
          title: tr('tour.check_title'),
          text: tr('tour.check_text'),
          place: 'bottom',
        },
      ],
    },
  };

  const BY_SCREEN = {};
  Object.entries(CHAPTERS).forEach(([id, chapter]) => {
    (BY_SCREEN[chapter.screen] ||= []).push(id);
  });

  // ---------- DOM helpers ----------

  const visible = (el) => {
    if (!el || el.closest('[hidden]')) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  };
  // Document-wide: in the app shell some targets live in the shell bars, outside the funnel.
  const find = (sel) => {
    for (const el of document.querySelectorAll(sel)) if (visible(el)) return el;
    return null;
  };
  const currentScreen = () => {
    const el = [...funnel.querySelectorAll('[data-vhp-screen]')].find((node) => !node.hidden);
    return el ? el.dataset.vhpScreen : null;
  };
  // Anything that takes over the screen: native dialogs (top layer), the swipe week sheet / success
  // screen, the swipe hint on the very first card.
  const blocked = () => {
    if (document.hidden) return true;
    if (document.querySelector('dialog[open]')) return true;
    if (funnel.querySelector('.vhp-sw-sheet:not([hidden]), .vhp-sw-success:not([hidden])')) return true;
    if (funnel.querySelector('[data-vhp-swipe-hint]:not([hidden])')) return true;
    if (funnel.querySelector('[data-vhp-screens].is-motion')) return true;
    return false;
  };

  // ---------- overlay ----------

  let root = null;
  let hole = null;
  let blur = null;
  let card = null;
  let titleEl = null;
  let textEl = null;
  let dotsEl = null;
  let skipBtn = null;
  let nextBtn = null;
  let counterEl = null;
  let session = null; // { id, steps, i, screen, opener }
  let raf = 0;

  const build = () => {
    root = document.createElement('div');
    root.className = 'vhp-tour';
    root.hidden = true;
    root.innerHTML =
      '<div class="vhp-tour__blocker"></div>' +
      '<div class="vhp-tour__blur" aria-hidden="true"><i></i><i></i><i></i><i></i></div>' +
      '<div class="vhp-tour__hole" aria-hidden="true"></div>' +
      '<div class="vhp-tour__card" role="dialog" aria-modal="true" aria-labelledby="vhp-tour-title" aria-describedby="vhp-tour-text">' +
      '<span class="vhp-tour__arrow" aria-hidden="true"></span>' +
      '<p class="vhp-tour__count"></p>' +
      '<h3 class="vhp-tour__title" id="vhp-tour-title"></h3>' +
      '<p class="vhp-tour__text" id="vhp-tour-text"></p>' +
      '<div class="vhp-tour__foot">' +
      '<div class="vhp-tour__dots" aria-hidden="true"></div>' +
      '<button type="button" class="vhp-tour__skip"></button>' +
      '<button type="button" class="vhp-tour__next"></button>' +
      '</div>' +
      '</div>';
    document.body.append(root);
    hole = root.querySelector('.vhp-tour__hole');
    blur = root.querySelector('.vhp-tour__blur');
    card = root.querySelector('.vhp-tour__card');
    titleEl = root.querySelector('.vhp-tour__title');
    textEl = root.querySelector('.vhp-tour__text');
    dotsEl = root.querySelector('.vhp-tour__dots');
    counterEl = root.querySelector('.vhp-tour__count');
    skipBtn = root.querySelector('.vhp-tour__skip');
    nextBtn = root.querySelector('.vhp-tour__next');
    skipBtn.addEventListener('click', () => finish(false));
    nextBtn.addEventListener('click', () => advance());
    card.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const items = [nextBtn, skipBtn].filter((btn) => !btn.hidden);
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const placeCard = (rect, place) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(320, vw - MARGIN * 2);
    card.style.width = `${width}px`;
    const height = card.offsetHeight;
    if (!rect) {
      card.dataset.side = 'center';
      card.style.left = `${Math.round((vw - width) / 2)}px`;
      card.style.top = `${Math.max(MARGIN, Math.round((vh - height) / 2))}px`;
      return;
    }
    const below = vh - rect.bottom - PAD - GAP;
    const above = rect.top - PAD - GAP;
    let side = place === 'top' || place === 'bottom' ? place : null;
    if (!side) side = below >= height || below >= above ? 'bottom' : 'top';
    if (side === 'bottom' && below < height && above > below) side = 'top';
    if (side === 'top' && above < height && below > above) side = 'bottom';
    let top = side === 'bottom' ? rect.bottom + PAD + GAP : rect.top - PAD - GAP - height;
    top = Math.min(Math.max(MARGIN, top), Math.max(MARGIN, vh - height - MARGIN));
    const mid = rect.left + rect.width / 2;
    const left = Math.min(Math.max(MARGIN, mid - width / 2), vw - width - MARGIN);
    card.dataset.side = side;
    card.style.left = `${Math.round(left)}px`;
    card.style.top = `${Math.round(top)}px`;
    const arrowX = Math.min(Math.max(22, mid - left), width - 22);
    card.style.setProperty('--vhp-tour-arrow-x', `${Math.round(arrowX)}px`);
  };

  // Blur panels around the target (top, bottom, left, right) so the target itself stays sharp. The panels are
  // anchored to the viewport edges in CSS and only read the target box from custom properties, so they
  // always reach the real edge (scrollbar-safe) and animate in sync.
  const placeBlur = (rect) => {
    const set = (name, value) => blur.style.setProperty(name, `${Math.round(value)}px`);
    set('--tx', rect.left - PAD);
    set('--ty', rect.top - PAD);
    set('--tw', rect.width + PAD * 2);
    set('--th', rect.height + PAD * 2);
  };

  const position = () => {
    raf = 0;
    if (!session) return;
    const step = session.steps[session.i];
    const target = step.sel ? find(step.sel) : null;
    if (step.sel && !target) {
      // The target vanished (re-render, screen change): move on, or end if nothing is left.
      advance(true);
      return;
    }
    if (!target) {
      hole.hidden = true;
      blur.classList.add('is-all');
      placeCard(null);
      return;
    }
    const rect = target.getBoundingClientRect();
    const r = parseFloat(getComputedStyle(target).borderTopLeftRadius) || 0;
    hole.hidden = false;
    hole.style.top = `${Math.round(rect.top - PAD)}px`;
    hole.style.left = `${Math.round(rect.left - PAD)}px`;
    hole.style.width = `${Math.round(rect.width + PAD * 2)}px`;
    hole.style.height = `${Math.round(rect.height + PAD * 2)}px`;
    hole.style.borderRadius = `${Math.round(r + PAD)}px`;
    blur.classList.remove('is-all');
    placeBlur(rect);
    placeCard(rect, step.place);
  };
  const schedule = () => {
    if (!raf) raf = window.requestAnimationFrame(position);
  };

  const renderStep = () => {
    const step = session.steps[session.i];
    const n = session.steps.length;
    const last = session.i === n - 1;
    titleEl.textContent = step.title;
    textEl.textContent = step.text;
    counterEl.hidden = Boolean(step.welcome) || n < 2;
    counterEl.textContent = `${session.i + 1} / ${n}`;
    dotsEl.hidden = Boolean(step.welcome) || n < 2;
    dotsEl.replaceChildren(
      ...session.steps.map((_, index) => {
        const dot = document.createElement('i');
        if (index === session.i) dot.className = 'is-on';
        return dot;
      }),
    );
    nextBtn.textContent = step.welcome ? tr('tour.start') : last ? tr('tour.done') : tr('tour.next');
    skipBtn.textContent = step.welcome ? tr('tour.no_thanks') : tr('tour.skip');
    skipBtn.hidden = !step.welcome && last;
    card.classList.toggle('is-welcome', Boolean(step.welcome));
    root.classList.toggle('is-welcome', Boolean(step.welcome));
    const target = step.sel ? find(step.sel) : null;
    if (target) {
      const r = target.getBoundingClientRect();
      // A sticky screen footer (e.g. the preview CTA bar) covers the bottom of the viewport.
      const footer = find('.vhp-screen__footer');
      const bottom = footer ? footer.getBoundingClientRect().top : window.innerHeight;
      const fits = r.top >= 0 && r.bottom <= bottom;
      if (!fits) target.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
    }
    position();
    // Smooth scrolling and card re-layout settle a moment later.
    window.setTimeout(schedule, reduced() ? 0 : 380);
    nextBtn.focus({ preventScroll: true });
    if (step.enter) {
      const at = session.i;
      window.setTimeout(() => {
        if (session && session.i === at) step.enter();
      }, reduced() ? 0 : 500);
    }
  };

  const hasTarget = (step) => !step.sel || Boolean(find(step.sel));

  const open = (id, { force = false } = {}) => {
    const chapter = CHAPTERS[id];
    if (!chapter || session) return false;
    if (chapter.paid != null && chapter.paid !== paid()) return false;
    if (!force && (memo.off || memo.done[id])) return false;
    const steps = chapter.steps.filter(hasTarget);
    if (!steps.length) return false;
    if (!root) build();
    session = { id, steps, i: 0, screen: chapter.screen, opener: document.activeElement };
    root.hidden = false;
    document.documentElement.classList.add('vhp-tour-on');
    // First step: place the hole without sliding in from the corner.
    hole.style.transition = 'none';
    blur.classList.add('is-still');
    renderStep();
    window.requestAnimationFrame(() => {
      root.classList.add('is-on');
      window.requestAnimationFrame(() => {
        hole.style.transition = '';
        blur.classList.remove('is-still');
      });
    });
    return true;
  };

  const close = () => {
    const opener = session?.opener;
    session = null;
    if (!root) return;
    window.cancelAnimationFrame(raf);
    raf = 0;
    root.classList.remove('is-on');
    root.hidden = true;
    document.documentElement.classList.remove('vhp-tour-on');
    if (opener && opener.isConnected && typeof opener.focus === 'function') {
      try {
        opener.focus({ preventScroll: true });
      } catch (e) {
        /* ignore */
      }
    }
  };

  // completed: true when the user reached the last step; false on skip. Both mark the chapter done.
  const finish = (completed) => {
    if (!session) return;
    const { id, steps, i } = session;
    const welcome = Boolean(steps[i]?.welcome);
    markDone(id);
    if (welcome && !completed) {
      // "Nein, danke" on the welcome card turns the whole tour off.
      memo.off = true;
      write();
    }
    close();
    if (welcome && completed) open('week', { force: true });
  };

  function advance(skipMissing) {
    if (!session) return;
    const step = session.steps[session.i];
    if (step.welcome) {
      finish(true);
      return;
    }
    let next = session.i + 1;
    while (next < session.steps.length && !hasTarget(session.steps[next])) next += 1;
    if (next >= session.steps.length) {
      finish(!skipMissing || session.i > 0);
      return;
    }
    session.i = next;
    renderStep();
  }

  // ---------- triggering ----------

  let timer = 0;
  let tries = 0;
  const clearTimer = () => {
    window.clearTimeout(timer);
    timer = 0;
  };

  const tryStart = () => {
    timer = 0;
    if (session || memo.off) return;
    const screen = currentScreen();
    const ids = BY_SCREEN[screen] || [];
    const pending = ids.find((id) => {
      const chapter = CHAPTERS[id];
      return (chapter.paid == null || chapter.paid === paid()) && !memo.done[id];
    });
    if (!pending) return;
    // Paid users editing macros/eckdaten from Ziele are not in setup.
    if (screen === 'macros' && paid()) return;
    if (!blocked() && open(pending)) return;
    tries += 1;
    if (tries < RETRY_MAX) timer = window.setTimeout(tryStart, RETRY_MS);
  };

  funnel.addEventListener('vhp:render', (event) => {
    const screen = event.detail?.screen;
    if (session) {
      if (screen !== session.screen) close();
      else schedule();
      return;
    }
    clearTimer();
    tries = 0;
    timer = window.setTimeout(tryStart, SETTLE_MS);
  });

  window.addEventListener('resize', schedule);
  window.addEventListener('scroll', schedule, true);
  window.addEventListener('keydown', (event) => {
    if (session && event.key === 'Escape') {
      event.preventDefault();
      finish(false);
    }
  });
  window.addEventListener('pagehide', () => {
    if (session) close();
  });

  // Replay button in the Dein Plan sheet: forget progress, go to the Wochenplan, the week chapter starts there.
  funnel.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-vhp-tour-replay]');
    if (!btn) return;
    memo = blank();
    memo.done.welcome = 1;
    write();
    const week = funnel.querySelector('[data-vhp-tab="week"]');
    if (week) week.click();
  });

  window.vhpPlannerTour = {
    start(id = 'week') {
      memo.off = false;
      write();
      return open(id, { force: true });
    },
    reset() {
      memo = blank();
      write();
    },
    chapters: () => Object.keys(CHAPTERS),
  };

  // First screen is already rendered by the time this script runs.
  const first = currentScreen();
  if (first) funnel.dispatchEvent(new CustomEvent('vhp:render', { detail: { screen: first } }));
})();
